// @ts-check
/* CUSTOM: "Fuldend looket" (snippets/custom-complete-the-look.liquid).
   - Formularer med [data-ctl-form] lægges i kurven via /cart/add.js. Bagefter sendes Motions eget
     "ajaxProduct:added" fra formularen → Motion genopbygger og åbner kurveskuffen (gaveæske-popuppen
     reagerer også, som ved alle andre tilføjelser).
   - Knapper med [data-ctl-open] åbner en <dialog> med variantvalg (sideskuffe desktop / bundark mobil).
   Ren logik ligger i customCtlErrorMessage() og customCtlSubmitState() – testbar via
   customElements.get('custom-complete-the-look').logic. */

/**
 * Tekst til fejlbesked ud fra svaret fra /cart/add.js.
 * @param {any} data JSON-svar (eller null ved netværksfejl)
 * @param {string} fallback standardtekst
 * @returns {string}
 */
function customCtlErrorMessage(data, fallback) {
  if (data && typeof data.description === 'string' && data.description.trim()) return data.description.trim();
  if (data && typeof data.message === 'string' && data.message.trim()) return data.message.trim();
  return fallback;
}

/**
 * Skal "Læg i kurv" i skuffen være aktiv, og hvilken pris skal vises?
 * @param {{ checked: boolean, disabled: boolean, price: string }[]} options
 * @returns {{ enabled: boolean, price: string | null }}
 */
function customCtlSubmitState(options) {
  const chosen = options.find((o) => o.checked && !o.disabled);
  return chosen ? { enabled: true, price: chosen.price || null } : { enabled: false, price: null };
}

/**
 * GA4-formet dataLayer-event (samme mønster som gaveæskens gift_wrap_*).
 * @param {string} event fx 'complete_the_look_add'
 * @param {{ id: string, title: string, variantId: string, variantTitle?: string, price: number }[]} items
 * @param {{ currency: string, source: string }} ctx
 */
function customCtlTrackPayload(event, items, ctx) {
  const list = items.map((it, i) => ({
    item_id: it.id,
    item_name: it.title,
    item_variant: it.variantTitle || undefined,
    item_list_id: 'fuldend_looket',
    item_list_name: 'Fuldend looket',
    index: i,
    price: Number(it.price) || 0,
    quantity: 1,
    variant_id: it.variantId,
  }));
  return {
    event,
    complete_the_look: { source_product: ctx.source },
    ecommerce: {
      currency: ctx.currency,
      value: list.reduce((sum, it) => sum + it.price, 0),
      items: list,
    },
  };
}

class CustomCompleteTheLook extends HTMLElement {
  static logic = { customCtlErrorMessage, customCtlSubmitState, customCtlTrackPayload };

  constructor() {
    super();
    /** @type {HTMLElement | null} */
    this.lastTrigger = null;
    this.onClick = this.onClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('change', this.onChange);
    this.observeView();
    this.querySelectorAll('dialog').forEach((d) => {
      // Esc: luk med animation. Lyt også på keydown – andre scripts på siden kan stoppe Esc,
      // og så sender browseren aldrig 'cancel'.
      d.addEventListener('cancel', (e) => { e.preventDefault(); this.closeSheet(/** @type {HTMLDialogElement} */ (d)); });
      d.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        e.preventDefault();
        this.closeSheet(/** @type {HTMLDialogElement} */ (d));
      });
      // Klik på den mørke baggrund (uden for panelet) lukker
      d.addEventListener('click', (e) => { if (e.target === d) this.closeSheet(/** @type {HTMLDialogElement} */ (d)); });
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('change', this.onChange);
    this.unlockScroll();
  }

  /** @param {MouseEvent} e */
  onClick(e) {
    const target = /** @type {HTMLElement} */ (e.target);
    const link = target.closest('a[href]');
    if (link && !link.closest('dialog')) {
      const li = /** @type {HTMLElement | null} */ (link.closest('[data-ctl-item]'));
      if (li) this.track('complete_the_look_click', [this.itemData(li)]);
    }
    const opener = /** @type {HTMLElement | null} */ (target.closest('[data-ctl-open]'));
    if (opener) {
      const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById(opener.dataset.ctlOpen || ''));
      if (dialog) this.openSheet(dialog, opener);
      return;
    }
    const closer = target.closest('[data-ctl-close]');
    if (closer) {
      const dialog = /** @type {HTMLDialogElement | null} */ (closer.closest('dialog'));
      if (dialog) this.closeSheet(dialog);
    }
  }

  /** @param {Event} e */
  onChange(e) {
    const input = /** @type {HTMLInputElement} */ (e.target);
    if (input.type !== 'radio') return;
    const form = /** @type {HTMLFormElement | null} */ (input.form);
    if (!form) return;
    const radios = /** @type {HTMLInputElement[]} */ ([...form.querySelectorAll('input[type="radio"][name="id"]')]);
    const state = customCtlSubmitState(radios.map((r) => ({ checked: r.checked, disabled: r.disabled, price: r.dataset.price || '' })));
    const submit = /** @type {HTMLButtonElement | null} */ (form.querySelector('[data-ctl-submit]'));
    const label = form.querySelector('[data-ctl-submit-text]');
    const price = form.querySelector('[data-ctl-price]');
    if (submit) submit.disabled = !state.enabled;
    if (label && state.enabled) label.textContent = this.dataset.addText || '';
    if (price && state.price) price.textContent = state.price;
    this.showError(form, '');
  }

  /** @param {SubmitEvent} e */
  async onSubmit(e) {
    const form = /** @type {HTMLFormElement} */ (e.target);
    if (!form.matches('[data-ctl-form]')) return;
    e.preventDefault();
    if (form.dataset.busy === 'true') return; // dobbelttryk
    const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
    const formData = new FormData(form);
    if (!formData.get('id')) return;

    form.dataset.busy = 'true';
    button?.setAttribute('aria-busy', 'true');
    this.showError(form, '');

    /** @type {any} */
    let data = null;
    let ok = false;
    try {
      const res = await fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      data = await res.json().catch(() => null);
      ok = res.ok;
    } catch (err) {
      ok = false;
    }

    form.dataset.busy = 'false';
    button?.removeAttribute('aria-busy');

    if (!ok) {
      this.showError(form, customCtlErrorMessage(data, this.dataset.errorText || ''));
      return;
    }

    const li = /** @type {HTMLElement | null} */ (form.closest('[data-ctl-item]'));
    if (li) {
      const chosen = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]:checked'));
      this.track('complete_the_look_add', [this.itemData(li, chosen)]);
    }

    const dialog = /** @type {HTMLDialogElement | null} */ (form.closest('dialog'));
    if (dialog) this.closeSheet(dialog);
    this.markAdded(dialog ? this.lastTrigger : button);

    // @ts-ignore – Motions globale tema-indstillinger
    if (window.theme?.settings?.cartType === 'page') {
      window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
      return;
    }
    form.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true, detail: { product: data } }));
  }

  /**
   * Produktdata til tracking fra rækken (og evt. valgt variant i skuffen).
   * @param {HTMLElement} li
   * @param {HTMLInputElement | null} [chosen]
   */
  itemData(li, chosen = null) {
    return {
      id: li.dataset.productId || '',
      title: li.dataset.productTitle || '',
      variantId: chosen ? chosen.value : li.dataset.variantId || '',
      variantTitle: chosen ? chosen.dataset.variantTitle : undefined,
      price: Number(chosen ? chosen.dataset.trackPrice : li.dataset.trackPrice) || 0,
    };
  }

  /**
   * Send event til GTM (dataLayer) og Shopify Customer Events (custom pixels).
   * @param {string} event
   * @param {ReturnType<CustomCompleteTheLook['itemData']>[]} items
   */
  track(event, items) {
    const payload = customCtlTrackPayload(event, items, { currency: this.dataset.currency || 'DKK', source: this.dataset.sourceProduct || '' });
    try {
      // @ts-ignore – GTM
      window.dataLayer = window.dataLayer || [];
      // @ts-ignore
      window.dataLayer.push({ ecommerce: null }); // nulstil forrige ecommerce-objekt (GA4-anbefaling)
      // @ts-ignore
      window.dataLayer.push(payload);
      // @ts-ignore – Shopify Customer Events
      window.Shopify?.analytics?.publish?.(event, payload);
    } catch (err) {
      /* tracking må aldrig stoppe køb */
    }
  }

  // Ét "view"-event pr. sidevisning, når halvdelen af blokken er synlig
  observeView() {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((en) => en.isIntersecting)) return;
      io.disconnect();
      const lis = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-ctl-item]')]);
      this.track('complete_the_look_view', lis.map((li) => this.itemData(li)));
    }, { threshold: 0.5 });
    io.observe(this);
  }

  /**
   * Kort "tilføjet"-tilstand på knappen.
   * @param {HTMLElement | null} button
   */
  markAdded(button) {
    if (!button) return;
    const label = button.getAttribute('aria-label') || '';
    button.classList.add('is-added');
    button.setAttribute('aria-label', this.dataset.addedText || label);
    window.setTimeout(() => {
      button.classList.remove('is-added');
      button.setAttribute('aria-label', label);
    }, 2000);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} message
   */
  showError(form, message) {
    const el = /** @type {HTMLElement | null} */ (
      form.querySelector('[data-ctl-error]') || form.closest('[data-ctl-item]')?.querySelector('[data-ctl-error]') || null
    );
    if (!el) return;
    el.textContent = message; // aldrig innerHTML
    el.hidden = !message;
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {HTMLElement} trigger
   */
  openSheet(dialog, trigger) {
    if (dialog.open) return;
    this.lastTrigger = trigger;
    dialog.showModal();
    this.lockScroll();
    /** @type {HTMLElement | null} */ (dialog.querySelector('[data-ctl-close]'))?.focus({ preventScroll: true });
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.setAttribute('data-open', '')));
  }

  /** @param {HTMLDialogElement} dialog */
  closeSheet(dialog) {
    if (!dialog.open) return;
    dialog.removeAttribute('data-open');
    const finish = () => {
      if (!dialog.open) return;
      dialog.close();
      this.unlockScroll();
      this.lastTrigger?.focus({ preventScroll: true });
    };
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return finish();
    dialog.addEventListener('transitionend', finish, { once: true });
    window.setTimeout(finish, 450); // hvis transitionend ikke kommer
  }

  // <dialog> låser ikke siden bagved (iOS). Samme lås som filterskuffen: html hidden + body clip.
  // overflow:hidden på <body> ville gøre body til scroll-container, så Motions sticky galleri hoppede ud af syne.
  lockScroll() {
    document.documentElement.classList.add('custom-ctl-lock');
  }

  unlockScroll() {
    document.documentElement.classList.remove('custom-ctl-lock');
  }
}

if (!customElements.get('custom-complete-the-look')) {
  customElements.define('custom-complete-the-look', CustomCompleteTheLook);
}
